<script lang="ts">
  import { onMount, onDestroy } from 'svelte'
  import { toastsStore, removeToast, type Toast } from '$lib/stores/toast'

  let toasts = $state<Toast[]>([])
  
  // Subscribe to toasts store changes
  const unsubscribe = toastsStore.subscribe(value => {
    toasts = value
  })
  
  function handleClose(id: string) {
    removeToast(id)
  }

  function getTypeClass(type: Toast['type']): string {
    switch (type) {
      case 'success':
        return 'toast-success'
      case 'error':
        return 'toast-error'
      case 'warning':
        return 'toast-warning'
      default:
        return 'toast-info'
    }
  }

  function getTypeIcon(type: Toast['type']): string {
    switch (type) {
      case 'success':
        return '✓'
      case 'error':
        return '✕'
      case 'warning':
        return '⚠'
      default:
        return 'i'
    }
  }

  // Auto-cleanup on unmount
  onDestroy(() => {
    if (unsubscribe) {
      unsubscribe()
    }
    // Don't clear toasts in test environment to avoid interfering with tests
    if (typeof process === 'undefined' || !process.env?.VITEST) {
      toastsStore.set([])
    }
  })
</script>

<!-- Toast container positioned at bottom-right -->
<div class="toast-container">
  {#each toasts as toast (toast.id)}
    <div 
      class="toast {getTypeClass(toast.type)}"
      role="alert"
      aria-live="polite"
    >
      <div class="toast-icon">
        {getTypeIcon(toast.type)}
      </div>
      
      <div class="toast-content">
        <span class="toast-message">{toast.message}</span>
      </div>
      
      <button 
        class="toast-close"
        onclick={() => handleClose(toast.id)}
        aria-label="닫기"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
    </div>
  {/each}
</div>

<style>
  .toast-container {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 420px;
    width: calc(100% - 48px);
    pointer-events: none; /* Allow clicks through container */
  }

  .toast {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 18px;
    padding-right: 36px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05);
    border: 1px solid;
    backdrop-filter: blur(10px);
    pointer-events: auto; /* Enable clicks on individual toasts */
    animation: slideInRight 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    transition: all 0.2s ease;
    position: relative;
    max-width: 100%;
    word-wrap: break-word;
    min-height: 52px;
  }

  .toast:hover {
    transform: translateX(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .toast-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    font-size: 11px;
    font-weight: bold;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .toast-content {
    flex: 1;
    min-width: 0; /* Allow text to wrap */
  }

  .toast-message {
    display: block;
    color: inherit;
  }

  .toast-close {
    position: absolute;
    top: 8px;
    right: 8px;
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: currentColor;
    opacity: 0.5;
    transition: opacity 0.2s ease, background-color 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    flex-shrink: 0;
  }

  .toast-close:hover {
    opacity: 0.8;
    background: rgba(0, 0, 0, 0.1);
  }

  .toast-close:active {
    opacity: 1;
  }

  /* Toast type styles */
  .toast-info {
    background: var(--bg-secondary);
    color: var(--text-primary);
    border-color: var(--border-secondary);
  }

  .toast-info .toast-icon {
    background: var(--accent-primary);
    color: white;
  }

  .toast-success {
    background: rgba(22, 163, 74, 0.1);
    color: var(--success);
    border-color: var(--success);
  }

  .toast-success .toast-icon {
    background: var(--success);
    color: white;
  }

  .toast-error {
    background: rgba(220, 38, 38, 0.1);
    color: var(--error);
    border-color: var(--error);
  }

  .toast-error .toast-icon {
    background: var(--error);
    color: white;
  }

  .toast-warning {
    background: rgba(234, 88, 12, 0.1);
    color: var(--warning);
    border-color: var(--warning);
  }

  .toast-warning .toast-icon {
    background: var(--warning);
    color: white;
  }

  /* Dark theme adjustments */
  @media (prefers-color-scheme: dark) {
    .toast {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
    }
    
    .toast:hover {
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.5);
    }
    
    .toast-info {
      background: var(--bg-tertiary);
      border-color: var(--border-secondary);
    }
    
    .toast-close:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .toast-success {
      background: rgba(34, 197, 94, 0.15);
    }

    .toast-error {
      background: rgba(239, 68, 68, 0.15);
    }

    .toast-warning {
      background: rgba(245, 158, 11, 0.15);
    }
  }

  :global(.dark-theme) .toast {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  }
  
  :global(.dark-theme) .toast:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.5);
  }
  
  :global(.dark-theme) .toast-info {
    background: var(--bg-tertiary);
    border-color: var(--border-secondary);
  }
  
  :global(.dark-theme) .toast-close:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  :global(.dark-theme) .toast-success {
    background: rgba(34, 197, 94, 0.15);
  }

  :global(.dark-theme) .toast-error {
    background: rgba(239, 68, 68, 0.15);
  }

  :global(.dark-theme) .toast-warning {
    background: rgba(245, 158, 11, 0.15);
  }

  /* Mobile responsive */
  @media (max-width: 768px) {
    .toast-container {
      bottom: 16px;
      right: 16px;
      left: 16px;
      max-width: none;
    }

    .toast {
      padding: 10px 14px;
      font-size: 13px;
      gap: 10px;
    }

    .toast-icon {
      width: 16px;
      height: 16px;
      font-size: 10px;
    }

    .toast-close {
      top: 6px;
      right: 6px;
      padding: 3px;
    }
  }

  /* Tablet responsive */
  @media (max-width: 1024px) and (min-width: 769px) {
    .toast-container {
      bottom: 18px;
      right: 18px;
      max-width: 320px;
    }
  }
</style>